import { compile } from 'path-to-regexp';

const BasePaths = {
  account: '/apis/accounts',
  audenc: '/apis/audenc',
  ca: '/apis/ca',
  ems: '/apis/ems',
  essences: '/apis/essences',
  hostnamevalidate: '/apis/hostnamevalidate',
  identity: '/apis/identity',
  licenses: '/apis/licenses',
  logos: '/apis/logos',
  logosettings: '/apis/logosettings',
  metadata: '/apis/metadata',
  presets: '/apis/presets',
  services: '/apis/services',
  scte: '/apis/scte',
  streams: '/apis/streams',
  upgrade: '/apis/upgrade',
  videnc: '/apis/videnc',
};

const withBase = (base: string, path: string) => base.concat(path);

// This is just a helper method, but maybe we want to add some default options.
export const APIPathWithArguments = <T extends object>(path: string, data: T): string => compile(path)(data);

interface APIPathInterface {
  account: {
    add: string;
    delete: string;
    enable: string;
    get: string;
    list: string;
    lock: string;
    pubkey: string /*single item with id*/;
    pubkeys: string;
    unlock: string;
    update: string;
  };
  audenc: {
    get: string;
    list: string;
    mute: string;
    start: string;
    stats: string;
    stop: string;
    unmute: string;
    update: string;
  };
  audit: string;
  banner: string;
  ca: {
    index: string;
    delete: string;
    download: string;
  };
  comport: string;
  datetime: string;
  deleteFirmware: (filename: string) => string;
  ems: {
    decode: string;
    pair: string;
    unpair: string;
    unpairForce: string;
  };
  essences: {
    add: string;
    delete: string;
    get: string;
    list: string;
    resolutions: string;
    start: string;
    stats: string;
    stop: string;
    update: string;
  };
  identity: {
    default: string;
    delete: string;
    download: string;
    generate: string;
    index: string;
  };
  leds: string;
  licenses: {
    list: string;
    download: string;
    single: string;
  };
  license: {
    get: string;
  };
  logos: {
    add: string;
    all: string;
    delete: string;
    get: string;
  };
  logosettings: {
    all: string;
    update: string;
  };
  messages: string;
  metadata: {
    add: string;
    delete: string;
    get: string;
    list: string;
    start: string;
    stats: string;
    stop: string;
    update: string;
  };
  monitorFirmwareUpload: (filename: string) => string;
  network: string;
  ntpStatistics: string;
  onvif: string;
  policies: string;
  presets: {
    add: string;
    delete: string;
    download: string;
    duplicate: string;
    isSaved: string;
    list: string;
    load: string;
    rename: string;
    startup: string;
    update: string;
  };
  reboot: string;
  scte: {
    get: string;
    list: string;
    start: string;
    stats: string;
    stop: string;
    update: string;
  };
  services: {
    ems: string;
    nmos: string;
    preview: string;
    status: string;
    update: string;
  };
  session: string;
  streams: {
    list: string;
    single: string;
    start: string;
    stats: string;
    stop: string;
  };
  hostnamevalidate: {
    audit: string;
    metadatasource: string;
    stream: string;
    nmos: string;
  };
  system: string;
  systemInfo: string;
  status: {
    get: string;
    snapshot: string;
  };
  upgrade: {
    cancel: string;
    get: string;
    notify: string;
    upload: string;
    install: string;
  };
  validateNtpServer: string;
  videnc: {
    get: string;
    list: string;
    resolutions: string;
    start: string;
    stats: string;
    stop: string;
    update: string;
  };
  vidin: string;
}

export const APIPath: APIPathInterface = {
  account: {
    add: BasePaths.account,
    delete: withBase(BasePaths.account, '/:name'),
    enable: withBase(BasePaths.account, '/:name/enable'),
    get: withBase(BasePaths.account, '/:name'),
    list: BasePaths.account,
    lock: withBase(BasePaths.account, '/:name/lock'),
    pubkey: withBase(BasePaths.account, '/:name/pubkeys/:pubkey'),
    pubkeys: withBase(BasePaths.account, '/:name/pubkeys'),
    unlock: withBase(BasePaths.account, '/:name/unlock'),
    update: withBase(BasePaths.account, '/:name'),
  },
  audenc: {
    get: withBase(BasePaths.audenc, '/:id'),
    list: BasePaths.audenc,
    mute: withBase(BasePaths.audenc, '/:id/mute'),
    start: withBase(BasePaths.audenc, '/:id/start'),
    stats: withBase(BasePaths.audenc, '/:id/stats'),
    stop: withBase(BasePaths.audenc, '/:id/stop'),
    unmute: withBase(BasePaths.audenc, '/:id/unmute'),
    update: withBase(BasePaths.audenc, '/:id'),
  },
  audit: '/apis/audit',
  banner: '/apis/banner',
  ca: {
    index: BasePaths.ca,
    delete: withBase(BasePaths.ca, '/:name'),
    download: withBase(BasePaths.ca, '/:name'),
  },
  comport: '/apis/comport',
  datetime: '/apis/datetime',
  deleteFirmware: (filename: string) => `/apis/upgrade/${filename}`,
  ems: {
    decode: withBase(BasePaths.ems, '/decode'),
    pair: withBase(BasePaths.ems, '/pair'),
    unpair: withBase(BasePaths.ems, '/unpair'),
    unpairForce: withBase(BasePaths.ems, '/unpair/force'),
  },
  essences: {
    add: BasePaths.essences,
    delete: withBase(BasePaths.essences, '/:id'),
    get: withBase(BasePaths.essences, '/:id'),
    list: BasePaths.essences,
    resolutions: withBase(BasePaths.essences, '/resolutions'),
    start: withBase(BasePaths.essences, '/:id/start'),
    stats: withBase(BasePaths.essences, '/:id/stats'),
    stop: withBase(BasePaths.essences, '/:id/stop'),
    update: withBase(BasePaths.essences, '/:id'),
  },
  identity: {
    default: withBase(BasePaths.identity, '/:name/default'),
    delete: withBase(BasePaths.identity, '/:name'),
    download: withBase(BasePaths.identity, '/:name'),
    generate: BasePaths.identity,
    index: BasePaths.identity,
  },
  leds: '/apis/leds',
  licenses: {
    list: BasePaths.licenses,
    download: withBase(BasePaths.licenses, '/:name'),
    single: withBase(BasePaths.licenses, '/:name'),
  },
  license: {
    get: '/apis/license',
  },
  logos: {
    add: BasePaths.logos,
    all: BasePaths.logos,
    delete: withBase(BasePaths.logos, '/:name'),
    get: withBase(BasePaths.logos, '/:name'),
  },
  logosettings: {
    all: BasePaths.logosettings,
    update: withBase(BasePaths.logosettings, '/:interface'),
  },
  messages: '/apis/messages',
  metadata: {
    add: BasePaths.metadata,
    delete: withBase(BasePaths.metadata, '/:id'),
    get: withBase(BasePaths.metadata, '/:id'),
    list: BasePaths.metadata,
    start: withBase(BasePaths.metadata, '/:id/start'),
    stats: withBase(BasePaths.metadata, '/:id/stats'),
    stop: withBase(BasePaths.metadata, '/:id/stop'),
    update: withBase(BasePaths.metadata, '/:id'),
  },
  monitorFirmwareUpload: (filename: string) => `/${filename}`,
  network: '/apis/network',
  ntpStatistics: 'apis/chronyd',
  onvif: '/apis/onvif',
  policies: 'apis/policies',
  presets: {
    add: BasePaths.presets,
    delete: withBase(BasePaths.presets, '/:name'),
    download: withBase(BasePaths.presets, '/:name'),
    duplicate: withBase(BasePaths.presets, '/:name/duplicate'),
    isSaved: withBase(BasePaths.presets, '/current/issaved'),
    list: BasePaths.presets,
    load: withBase(BasePaths.presets, '/:name/load'),
    rename: withBase(BasePaths.presets, '/:name/rename'),
    startup: withBase(BasePaths.presets, '/:name/startup'),
    update: withBase(BasePaths.presets, '/:name'),
  },
  reboot: '/apis/reboot',
  scte: {
    get: withBase(BasePaths.scte, '/:id'),
    list: BasePaths.scte,
    start: withBase(BasePaths.scte, '/:id/start'),
    stats: withBase(BasePaths.scte, '/:id/stats'),
    stop: withBase(BasePaths.scte, '/:id/stop'),
    update: withBase(BasePaths.scte, '/:id'),
  },
  services: {
    ems: '/apis/ems',
    nmos: '/apis/nmos',
    preview: withBase(BasePaths.services, '/preview'),
    status: withBase(BasePaths.services, '/status'),
    update: BasePaths.services,
  },
  session: '/apis/authentication',
  streams: {
    list: BasePaths.streams,
    single: withBase(BasePaths.streams, '/:id'),
    start: withBase(BasePaths.streams, '/:id/start'),
    stats: withBase(BasePaths.streams, '/:id/stats'),
    stop: withBase(BasePaths.streams, '/:id/stop'),
  },
  hostnamevalidate: {
    audit: withBase(BasePaths.hostnamevalidate, '/audit'),
    metadatasource: withBase(BasePaths.hostnamevalidate, '/metadatasource'),
    stream: withBase(BasePaths.hostnamevalidate, '/stream'),
    nmos: withBase(BasePaths.hostnamevalidate, '/nmos'),
  },
  system: '/apis/system',
  systemInfo: '/apis/system_info',
  status: {
    get: '/apis/status',
    snapshot: '/apis/start_system_snapshot',
  },
  upgrade: {
    cancel: withBase(BasePaths.upgrade, '/cancel'),
    get: BasePaths.upgrade,
    notify: withBase(BasePaths.upgrade, '/notify'),
    upload: withBase(BasePaths.upgrade, '/upload'),
    install: withBase(BasePaths.upgrade, '/install'),
  },
  validateNtpServer: '/apis/ntpvalidate',
  videnc: {
    get: withBase(BasePaths.videnc, '/:id'),
    list: BasePaths.videnc,
    resolutions: withBase(BasePaths.videnc, '/0/resolutions'),
    start: withBase(BasePaths.videnc, '/:id/start'),
    stats: withBase(BasePaths.videnc, '/:id/stats'),
    stop: withBase(BasePaths.videnc, '/:id/stop'),
    update: withBase(BasePaths.videnc, '/:id'),
  },
  vidin: '/apis/vidin',
};
